import { Injectable, computed, signal } from '@angular/core'

const STORAGE_KEY = 'gbc-text-scale'

/**
 * Paliers de l'échelle de texte, en % de la taille navigateur (16px).
 * 87.5% (14px) est la base de GBC — voir `html { font-size }` dans styles.scss.
 * Toutes les tailles rem de l'app (Tailwind + design-tokens) suivent ce facteur,
 * donc baisser le défaut densifie aussi bien le texte que les espacements.
 */
const SCALE_STEPS = [75, 81.25, 87.5, 93.75, 100, 106.25, 112.5] as const

const DEFAULT_INDEX = SCALE_STEPS.indexOf(87.5)

/**
 * Échelle de taille de texte globale, pilotée depuis le menu utilisateur (A− / A+).
 * Applique `--gbc-text-scale` sur <html> et persiste le choix en localStorage.
 */
@Injectable({ providedIn: 'root' })
export class TextSizeService {
  private readonly index = signal(this.restore())

  readonly canDecrease = computed(() => this.index() > 0)
  readonly canIncrease = computed(() => this.index() < SCALE_STEPS.length - 1)
  readonly isDefault = computed(() => this.index() === DEFAULT_INDEX)

  /** Pourcentage affiché à l'utilisateur — 100 % correspond à la taille par défaut. */
  readonly displayPercent = computed(() => Math.round((SCALE_STEPS[this.index()] / SCALE_STEPS[DEFAULT_INDEX]) * 100))

  constructor() {
    this.apply()
  }

  decrease(): void {
    if (this.canDecrease()) this.setIndex(this.index() - 1)
  }

  increase(): void {
    if (this.canIncrease()) this.setIndex(this.index() + 1)
  }

  reset(): void {
    this.setIndex(DEFAULT_INDEX)
  }

  private setIndex(i: number): void {
    this.index.set(i)
    try {
      localStorage.setItem(STORAGE_KEY, String(i))
    } catch {
      // stockage indisponible (navigation privée) : l'échelle reste appliquée pour la session
    }
    this.apply()
  }

  private apply(): void {
    document.documentElement.style.setProperty('--gbc-text-scale', `${SCALE_STEPS[this.index()]}%`)
  }

  private restore(): number {
    const raw = localStorage.getItem(STORAGE_KEY)
    const i = raw === null ? NaN : Number(raw)
    return Number.isInteger(i) && i >= 0 && i < SCALE_STEPS.length ? i : DEFAULT_INDEX
  }
}
